import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Group,
  Icon,
  Input,
  Skeleton,
} from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import type { StickerSetType } from '@halo/chat-sdk';
import React, {
  memo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useMemo,
  useDeferredValue,
} from 'react';
import { useInViewport, useMemoizedFn, useThrottleFn, useRequest } from 'ahooks';
// @ts-expect-error - CSS
import 'swiper/css';
// @ts-expect-error - CSS
import 'swiper/css/free-mode';
// @ts-expect-error - CSS
import 'swiper/css/mousewheel';
import SearchIcon from './icons/search.svg?react';

interface GroupSlideProps {
  icons: Record<string, React.ReactNode>;
  renderItem: (item: {
    id: string;
    icon: React.ReactNode;
    type: StickerSetType;
  }) => React.ReactNode;
  requestGroupItemIcon: (id: string) => Promise<void>;
  items: { id: string; name: string }[];
  container?: HTMLElement;
  onSelect?: (itemId: string, groupId: string, type: StickerSetType) => void;
  groupId: string;
  type: StickerSetType;
  cols?: number;
}
// GroupSlide component for Swiper
const GroupSlide = memo(function GroupSlide({
  icons,
  renderItem,
  requestGroupItemIcon,
  items = [],
  container,
  onSelect,
  groupId,
  type,
  cols = 5,
}: GroupSlideProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);

  // Use useInViewport to observe the entire Flex container, load all icons when container is in viewport
  // Note: If container is undefined, useInViewport will use the default viewport
  const [inViewport] = useInViewport(containerRef, {
    root: container || undefined,
  });

  // Reset loading state when container changes to ensure useInViewport updates correctly
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [container]);

  // Load all icons when container enters viewport
  useEffect(() => {
    // Ensure items have data and container is in viewport
    if (inViewport && !hasLoadedRef.current && items.length > 0) {
      // Container enters viewport, load all icons
      hasLoadedRef.current = true;
      items.forEach((item) => {
        // If icon already exists, no need to load
        if (icons[item.id]) return;

        // Check if already requested
        const element = containerRef.current?.querySelector(`[id="${item.id}"]`);
        if (element) {
          const request = Reflect.get(element, 'request');
          if (request) return;
          Reflect.set(element, 'request', true);
        }

        // Directly call request function, icon will be updated automatically inside the request function
        requestGroupItemIcon(item.id).catch((error) => {
          // Error can be logged
          console.error('Failed to load icon:', error);
        });
      });
    }
  }, [inViewport, items, icons, requestGroupItemIcon]);

  // Reset loading state when items change
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [items]);

  // Use useMemo to optimize rendering list
  const renderedItems = useMemo(() => {
    return items.map((item) => {
      return (
        <GridItem
          id={item.id}
          key={item.id}
          onClick={() => onSelect?.(item.id, groupId, type)}
          aspectRatio="1"
          cursor="pointer"
          _hover={{ bg: 'bg.muted' }}
          borderRadius="md"
        >
          {renderItem?.({
            id: item.id,
            icon: icons[item.id],
            type,
          })}
        </GridItem>
      );
    });
  }, [items, icons, renderItem, onSelect, groupId, type]);

  return (
    <Box ref={containerRef}>
      <Grid templateColumns={`repeat(${cols}, 1fr)`}>{renderedItems}</Grid>
      {items.length === 0 && <Skeleton w="full" h="16" />}
    </Box>
  );

  // return (
  //   <Flex ref={containerRef} flexWrap="wrap" gap="2.5">
  //     {renderedItems}
  //     {items.length === 0 && <Skeleton w="full" h="16" />}
  //   </Flex>
  // );
});

interface PanelRef {
  /**
   * @name reload
   * @description reload the data by group id
   */
  reload: (id: string) => void;
}

export interface Tab {
  id: StickerSetType;
  icon: React.ReactNode;
  cols: number;
  miniCols: number;
}

interface PanelProps {
  tabs?: Tab[];
  tab: StickerSetType;
  onTabChange?: (tab: StickerSetType) => void;

  /**
   * @name mini
   * @description mini mode
   */
  mini?: boolean;

  /**
   * @name search
   * @description search for groups
   */
  search?: {
    on: () => void;
    groups: () => void;
  };

  /**
   * @name icons
   * @description icons state from parent component
   */
  icons: Record<string, React.ReactNode>;

  renderItem: (item: {
    id: string;
    icon: React.ReactNode;
    type: StickerSetType;
  }) => React.ReactNode;
  /**
   * @name request
   * @description get icons data by group id
   */
  requestGroups: (tab: StickerSetType) => Promise<
    Array<{
      id: string;
      name: string;
      logo?: React.ReactNode;
    }>
  >;
  requestGroupItems: (groupId: string) => Promise<Array<{ id: string; name: string }>>;
  requestGroupItemIcon: (itemId: string) => Promise<void>;
  onSelect?: (itemId: string, groupId: string, type: StickerSetType) => void;
}

export const Panel = forwardRef<PanelRef, PanelProps>(function Panel(
  {
    tabs,
    tab,
    onTabChange,
    mini = false,
    icons,
    requestGroups,
    requestGroupItems,
    requestGroupItemIcon,
    renderItem,
    onSelect,
    search,
  },
  ref
) {
  const [tabSwiper, setTabSwiper] = useState<SwiperType | null>(null);
  const [contentSwiper, setContentSwiper] = useState<SwiperType | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [groupItems, setGroupItems] = useState<Record<string, { id: string; name: string }[]>>(
    {}
  );

  // Purpose: Defer non-urgent icon updates to prevent blocking
  // Reason: When icons update frequently, defer rendering to avoid stuttering
  const deferredIcons = useDeferredValue(icons);

  const tabConfig = useMemo(() => {
    return tabs?.find(({ id }) => id === tab);
  }, [tabs, tab]);

  const { run: handleContentSlideChange } = useThrottleFn(
    (activeIndex: number = 0) => {
      tabSwiper?.slideTo(activeIndex, 300);
      setActiveTab(activeIndex);
    },
    { wait: 300 }
  );

  useImperativeHandle(ref, () => ({
    reload: () => {},
  }));

  const { run, data: groups } = useRequest(requestGroups, {
    manual: true,
    // cacheKey: `emoji:groups:${tab}`,
    loadingDelay: 500,
  });

  const groupsPending = useMemo(() => {
    return groups === undefined || groups.length === 0;
  }, [groups]);

  useEffect(() => {
    run(tab);
  }, [tab, run]);

  const groupRefs = useRef<HTMLElement[]>([]);

  const handleGroupInViewPort = useMemoizedFn((entry: IntersectionObserverEntry) => {
    const id = entry.target.getAttribute('id');
    if (!id) return;
    const request = Reflect.get(entry.target, 'request');
    if (request) return;
    Reflect.set(entry.target, 'request', true);
    requestGroupItems(id).then((res) => {
      setGroupItems((groupItems) => ({ ...groupItems, [id]: res }));
    });
  });

  useInViewport(groupRefs.current, {
    callback: handleGroupInViewPort,
    root: contentSwiper?.el,
  });

  useEffect(() => {
    contentSwiper?.update();
  }, [groupItems, contentSwiper]);

  // TODO use Tab Component to replace the current tab component

  return (
    <Center>
      <Flex
        w={mini ? '304px' : '420px'}
        h={mini ? '400px' : '460px'}
        overflow="hidden"
        flexDirection="column"
        bg="whiteAlpha.80"
        shadow="md"
        rounded="lg"
        backdropFilter="blur(10px)"
      >
        {/* Groups - Thumbs Swiper */}
        <Box mt="1" mx="3" flex="none">
          {groupsPending && <Skeleton w="100%" h="10" />}
          {!groupsPending && (
            <Box>
              <Swiper
                modules={[FreeMode, Mousewheel]}
                slidesPerView="auto"
                freeMode={{ enabled: true }}
                mousewheel={{ enabled: true }}
                onSwiper={setTabSwiper}
                centeredSlides={true}
                centeredSlidesBounds={true}
              >
                {groups?.map((item, index) => (
                  <SwiperSlide key={item.id} style={{ width: 'auto' }}>
                    <Box
                      position="relative"
                      onClick={() => {
                        contentSwiper?.slideTo(index, 300);
                        setActiveTab(index);
                      }}
                      mr="2"
                    >
                      <Center
                        boxSize="10"
                        fontSize="2xl"
                        p="2"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'bg.muted' }}
                        color="fg"
                        userSelect="none"
                      >
                        {item.logo}
                      </Center>
                      <Box
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        h="1"
                        bg="brand"
                        borderRadius="full"
                        visibility={activeTab === index ? 'visible' : 'hidden'}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          )}
        </Box>
        {/* Search */}
        {search && (
          <Box
            mx="3"
            flex="none"
            mt="2.5"
            borderRadius="full"
            bg="bg.subtle"
            pl="3"
            pr="2"
            overflow="hidden"
          >
            <Flex alignItems="center" gap="1.5">
              <Icon flex="none" fontSize="md">
                <SearchIcon />
              </Icon>
              <Input flex="1" border="none" placeholder="Search" p="0" fontSize="md" />
              {groupsPending && <Skeleton w="40" h="8" />}
              {!groupsPending && (
                <Box flex="none" py="0.5" maxW={mini ? '40' : '64'}>
                  <Swiper
                    style={{ width: '100%' }}
                    modules={[FreeMode, Mousewheel]}
                    freeMode={{ enabled: true }}
                    mousewheel={{ enabled: true }}
                    slidesPerView="auto"
                  >
                    {groups?.map((item) => (
                      <SwiperSlide key={item.id} style={{ width: 'auto' }}>
                        <Center
                          boxSize="8"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: 'bg.muted' }}
                          fontSize="md"
                          color="fg"
                          userSelect="none"
                        >
                          {item.logo}
                        </Center>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              )}
            </Flex>
          </Box>
        )}
        {/* Main Content - Vertical Swiper with FreeMode */}
        <Box mx="3" mt="2.5" flex="1" overflow="hidden">
          {groupsPending && <Skeleton w="full" h="full" />}
          {mini && (
            <Swiper
              onSwiper={setContentSwiper}
              style={{ height: '100%' }}
              onSlideChange={({ activeIndex }) => {
                handleContentSlideChange(activeIndex);
              }}
            >
              {groups?.map((item, index) => (
                <SwiperSlide key={item.id} style={{ height: '100%' }}>
                  <Box
                    id={item.id}
                    ref={(el: HTMLElement) => {
                      groupRefs.current[index] = el;
                    }}
                    h="full"
                    overflowY="auto"
                    css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
                  >
                    <GroupSlide
                      container={contentSwiper?.el}
                      icons={deferredIcons}
                      renderItem={renderItem}
                      requestGroupItemIcon={requestGroupItemIcon}
                      items={groupItems[item.id]}
                      onSelect={onSelect}
                      groupId={item.id}
                      type={tab}
                      cols={mini ? tabConfig?.miniCols : tabConfig?.cols}
                    />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          {!mini && (
            <Box h="full">
              <Swiper
                onSwiper={setContentSwiper}
                modules={[FreeMode, Mousewheel]}
                style={{ height: '100%' }}
                freeMode={{
                  enabled: true,
                  sticky: false, // Disable sticky scrolling
                  momentumBounce: false, // Disable bounce effect
                }}
                mousewheel={{
                  enabled: true,
                  forceToAxis: true,
                  sensitivity: 1,
                  releaseOnEdges: true,
                }}
                direction="vertical"
                onSlideChange={({ activeIndex }) => {
                  handleContentSlideChange(activeIndex);
                }}
                slidesPerView="auto"
                cssMode={false} // Disable CSS mode
                watchSlidesProgress={false} // Disable progress watching
              >
                {groups?.map((item, index) => (
                  <SwiperSlide key={item.id} style={{ height: 'auto' }}>
                    <Box
                      id={item.id}
                      ref={(el: HTMLElement) => {
                        groupRefs.current[index] = el;
                      }}
                      fontSize="sm"
                      color="fg.muted"
                      fontWeight="semibold"
                      mb="2.5"
                    >
                      {item.name}
                    </Box>
                    <GroupSlide
                      container={contentSwiper?.el}
                      icons={deferredIcons}
                      renderItem={renderItem}
                      requestGroupItemIcon={requestGroupItemIcon}
                      items={groupItems[item.id]}
                      onSelect={onSelect}
                      groupId={item.id}
                      type={tab}
                      cols={mini ? tabConfig?.miniCols : tabConfig?.cols}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          )}
        </Box>
        {!mini && (
          <Box flex="none" pt="2.5" pb="2" borderTop="1px solid" borderColor="bg.subtle">
            <Flex alignItems="center" justifyContent="center">
              <Group attached>
                {tabs?.map(({ icon, id }) => (
                  <Button
                    borderRadius="full"
                    variant="outline"
                    fontSize="3xl"
                    px="5.5"
                    py="1"
                    key={id}
                    bgColor={tab === id ? 'whiteAlpha.50' : 'transparent'}
                    color={tab === id ? 'black' : 'fg.muted'}
                    onClick={() => tab !== id && onTabChange?.(id)}
                  >
                    {icon}
                  </Button>
                ))}
              </Group>
            </Flex>
          </Box>
        )}
      </Flex>
    </Center>
  );
});
